import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Save, Info, Eye, Edit } from 'lucide-react';
import { useTheme } from '../contexts/ThemeContext';
import api from '../utils/api';
import toast from 'react-hot-toast';
import MarkdownRenderer from './MarkdownRenderer';
import Layout from './Layout';

const AboutManagement: React.FC = () => {
  const navigate = useNavigate();
  const { themeClasses } = useTheme();
  const [aboutContent, setAboutContent] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [showPreview, setShowPreview] = useState(false);

  useEffect(() => {
    fetchAbout();
  }, []);

  const fetchAbout = async () => {
    try {
      setIsLoading(true);
      const response = await api.get('/admin/about');
      setAboutContent(response.data.aboutContent || '');
    } catch (error) {
      console.error('Error fetching about content:', error);
      toast.error('Failed to load about content');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSave = async () => {
    if (!aboutContent.trim()) {
      toast.error('About content cannot be empty');
      return;
    }

    try {
      setIsSaving(true);
      await api.put('/admin/about', { aboutContent });
      toast.success('About content updated successfully!');
    } catch (error) {
      console.error('Error updating about content:', error);
      toast.error('Failed to update about content');
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return (
      <Layout title="About Management" showBackButton onBack={() => navigate('/admin/users')}>
        <div className="p-4">
          <div className={`${themeClasses.card} p-8 text-center rounded-lg`}>
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500 mx-auto"></div>
            <p className={`${themeClasses.textSecondary} mt-4`}>Loading about content...</p>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout title="About Management" showBackButton onBack={() => navigate('/admin/users')}>

      {/* Content */}
      <div className="p-4">
        <div className={`${themeClasses.card} rounded-lg`}>
          <div className="flex items-center mb-4">
            <Info className={`w-5 h-5 ${themeClasses.textSecondary} mr-2`} />
            <h2 className={`text-lg font-semibold ${themeClasses.text}`}>About Us Content</h2>
          </div>
          
          <div className="mb-4">
            <div className="flex items-center justify-between mb-2">
              <label className={`block text-sm font-medium ${themeClasses.textSecondary}`}>
                Content (Markdown supported)
              </label>
              <button
                onClick={() => setShowPreview(!showPreview)}
                className={`${themeClasses.buttonSecondary} px-3 py-1.5 rounded-lg flex items-center text-sm`}
              >
                {showPreview ? (
                  <>
                    <Edit className="w-4 h-4 mr-1.5" />
                    Edit
                  </>
                ) : (
                  <>
                    <Eye className="w-4 h-4 mr-1.5" />
                    Preview
                  </>
                )}
              </button>
            </div>
            {showPreview ? (
              <div className={`${themeClasses.card} p-4 h-96 overflow-y-auto border ${themeClasses.border} rounded-lg`}>
                <MarkdownRenderer content={aboutContent || '*No content to preview*'} />
              </div>
            ) : (
              <textarea
                value={aboutContent}
                onChange={(e) => setAboutContent(e.target.value)}
                className={`${themeClasses.input} w-full h-96 resize-none rounded-lg px-3 py-2 font-mono text-sm`}
                placeholder="Enter the about us content here... (Markdown syntax supported)"
              />
            )}
            <p className={`text-xs ${themeClasses.textSecondary} mt-2`}>
              💡 Tip: Use Markdown syntax for formatting (headers, lists, links, etc.)
            </p>
          </div>

          <div className="flex justify-end space-x-3">
            <button
              onClick={() => navigate('/admin/users')}
              className={`${themeClasses.buttonSecondary} px-4 py-2 rounded-lg`}
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={isSaving}
              className={`${themeClasses.button} px-4 py-2 rounded-lg flex items-center ${
                isSaving ? 'opacity-50 cursor-not-allowed' : ''
              }`}
            >
              <Save className="w-4 h-4 mr-2" />
              {isSaving ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default AboutManagement;

